import Cookies from 'js-cookie';
import { resultContext } from '@coveo/atomic';
import { RsHTMLElement } from '../rs-htmlelement.js';
import styles from 'inline:./rs-result-card.scss';
import { isTouchDevice } from '../../../_imports/utils/utils.common.js';

export class RsResultCard extends RsHTMLElement {
    result;

    async connected() {
        this.result = await resultContext(this);
    }

    styles() {
        return styles;
    }

    async mounted() {
        this.initEvents();

        await this.initCardLinks();
    }

    get atomicResultLink() {
        return this.getRootNode().querySelector('.link-container atomic-result-link a');
    }

    get card() {
        return this.shadow.querySelector('.card');
    }

    get cardLink() {
        return this.card.querySelector('.card-link')
    }

    get downloadButton() {
        return this.card.querySelector('.download-button');
    }

    get fallbackThumbnail() {
        return this.getAttribute('fallback-thumbnail');
    }

    get hoverVideoUrl() {
        return this.result.raw.lowqualityvideourl;
    }

    get hoverVideoStart() {
        return parseInt(this.result.raw.videothumbnailtrimstart) || 0;
    }

    get hoverVideoEnd() {
        return parseInt(this.result.raw.videothumbnailtrimend) || 10;
    }

    get pretitle() {
        return this.result.raw.resourcetypenamesingular || this.result.raw.resourcetypename || this.result.raw.commonresourcetypename || this.result.raw.commonresourcetype;
    }

    get shortDescription() {
        return this.result.raw.shortdescription || this.result.raw.description;
    }

    get title() {
        return this.result.raw.maintitle || this.result.raw.title;
    }

    get videoId() {
        return this.result.raw.resourcevideoid;
    }

    get thumbnailUrl() {
        return this.result.raw.thumbnailurl || this.result.raw.featuredimageurl || this.fallbackThumbnail;
    }

    get thumbnail() {
        return this.shadow.querySelector('.thumbnail');
    }

    get video() {
        return this.shadow.querySelector('video');
    }

    async initCardLinks() {
        await this.waitForAtomicLink();

        let href = this.atomicResultLink.href;

        /*
            Some events registration page is not the landing on www
        */
        if (this.result.raw.registrationbehavior === 'external'
            && this.result.raw.registrationlink) {
            href = this.result.raw.registrationlink;
        }

        this.cardLink.setAttribute('href', href);

        this.cardLink.addEventListener('click', () => {
            if (this.cardLink.href) {
                window.location = this.cardLink.href;
            }
        });

        this.downloadButton?.addEventListener('click', () => {
            if (this.downloadButton.href) {
                window.location = this.downloadButton.href;
            }
        });

        /*
            Atomic automatically handles links, but we want control over it, so the href enforced by atomic is removed
        */
        this.atomicResultLink.removeAttribute('href');
    }

    initEvents() {
        this.initVideoHoverEvent();
    }

    initVideoHoverEvent() {
        if (!this.video) {
            return;
        }

        let isHover = false;

        const play = () => {
            if (!this.video.paused) {
                return;
            }

            if (isHover) {
                this.video.currentTime = this.hoverVideoStart;
                this.video.play()
                    .then(() => {
                        if (!isHover) {
                            this.video.pause();
                        } else {
                            this.thumbnail.classList.add('hover');
                        }
                    });
            }
        };

        this.card.addEventListener('mouseenter', async () => {
            isHover = true;

            if (!this.hoverVideoUrl) {
                return;
            }

            this.video.addEventListener('timeupdate', () => {
                if (this.video.currentTime > this.hoverVideoEnd) {
                    this.video.currentTime = this.hoverVideoStart;
                }
            });

            play();
        });

        this.card.addEventListener('mouseleave', () => {
            isHover = false;

            if (!this.video.paused) {
                this.video.pause();
            }

            this.thumbnail.classList.remove('hover');
        });
    }

    isCoveoEmployee() {
        return Cookies.get('interalE') && this.result.raw.resourcedocumentlink;
    }

    isTopResult() {
        return this.result.isTopResult;
    }

    isRecommendation() {
        return this.result.isRecommendation;
    }

    renderTag() {
        /*
            Recommendation tag has priority over featured
            Only one should display
        */
        if (this.isRecommendation()) {
            return this.renderRecommendationTag();
        } else if (this.isTopResult()) {
            return this.renderTopResultTag();
        }

        return '';
    }

    // Available to Coveo employees only (through C@C cookie)
    renderDownloadButton() {
        if (this.isCoveoEmployee()) {
            return `
                <div class="download-button-container">
                    <a class="download-button" href="${this.result.raw.resourcedocumentlink}">
                        <span class="download-button-label">${this.i18n.t('rs.resultCardDownloadLabel')}</span>
                        ${this.renderDownloadIcon()}
                    </div>
                </a>
            `;
        }

        return '';
    }

    renderRecommendationTag() {
        return `
            <div class="tag">
                <span>${this.i18n.t('rs.resultCardRecommendationLabel')}</span>
            </div>
        `;
    }

    renderTopResultTag() {
        return `
            <div class="tag">
                <span>${this.i18n.t('rs.resultCardTopResultLabel')}</span>
            </div>
        `;
    }

    renderDownloadIcon() {
        return ` 
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2L8 11" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11 8L8 11L5 8" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3 12V12C3 13.1046 3.89543 14 5 14H11C12.1046 14 13 13.1046 13 12V12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
    }

    renderPretitle() {
        if (this.pretitle) {
            return `<div class="pretitle">${this.pretitle}</div>`;
        }

        return '';
    }

    renderShortDescription() {
        if (this.shortDescription) {
            return `<div class="description">${this.shortDescription}</div>`;
        }

        return '';
    }

    renderTitle() {
        if (this.title) {
            return `<div class="title">${this.title}</div>`;
        }

        return '';
    }

    renderVideo() {
        if (!isTouchDevice() && this.videoId && this.hoverVideoUrl) {
            return `<video src="${this.hoverVideoUrl}" playsinline loop muted preload="metadata"></video>`;
        }

        return '';
    }

    renderVideoPlayButton() {
        if (this.videoId) {
            return `
                <div class="play-button">
                    <svg viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect id="Rectangle 886" x="0.5" width="71" height="71" rx="35.5" fill="#1169DA" fill-opacity="0.7" />
                        <path id="Rectangle 887" d="M27.5915 18.5316L51.6446 35.4161L27.5915 51.7895V35.4161L27.5915 18.5316Z" fill="white" />
                    </svg>
                </div>
            `;
        }

        return '';
    }

    renderThumbnail() {
        if (this.thumbnailUrl) {
            return `<img src="${this.thumbnailUrl}">`;
        }

        return '';
    }

    async waitForAtomicLink() {
        await customElements.whenDefined('atomic-result-link');

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (this.atomicResultLink) {
                    clearInterval(interval);
                    resolve();
                }
            }, 15);
        });
    }

    render() {
        // the empty <template></template> is there to prevent atomic-result-link to render its default value

        this.shadow.innerHTML = `
            <div class="card">
                <a class="card-link"></a>
                <div class="card-inner">
                    <div class="thumbnail">
                        ${this.renderVideo()}
                        ${this.renderThumbnail()}
                        ${this.renderVideoPlayButton()}
                    </div>
                    <div class="content">
                        <div class="content-head">
                            ${this.renderPretitle()}
                            ${this.renderTag()}
                            ${this.renderDownloadButton()}
                        </div>
                        ${this.renderTitle()}
                        ${this.renderShortDescription()}
                    </div>
                </div>
            </div>
        `;
    }
};